import { useState } from 'react';

import { StateBlock } from '@/components/reports/StateBlock';
import { Button } from '@/components/ui/button';
import { Field, FieldRow } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Table, TableWrap, Td, Th } from '@/components/ui/table';
import { ApiError, api, type IpAllowlistEntry } from '@/lib/api';
import { useApi } from '@/lib/useApi';

interface IpAllowlist {
  entries: IpAllowlistEntry[];
  /** いまこの画面を開いている端末の接続元。最初の 1 件を入れるのに要る。 */
  clientIp: string | null;
}

/**
 * IP 許可リスト。
 *
 * 1 件でも入れると、このテナントのログインと MCP の接続がその範囲からだけに
 * 制限される。**空は「制限なし」**なので、設定したつもりで空という誤解を
 * 作らないよう、0 件のときはその旨を明示する。
 *
 * 自分を締め出す設定は API が 400 で断る。理由をそのまま出したいので、
 * メッセージは加工しない。
 */
export function IpAllowlistSettings({ isAdmin }: { isAdmin: boolean }) {
  const { data, error, loading, reload } = useApi<IpAllowlist>('/api/tenant/ip-allowlist');
  const [message, setMessage] = useState<string | null>(null);

  async function run(action: () => Promise<unknown>, note: string) {
    setMessage(null);
    try {
      await action();
      setMessage(note);
      reload();
    } catch (cause) {
      setMessage(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    }
  }

  const entries = data?.entries ?? [];

  return (
    <section className="flex flex-col gap-4 border border-line bg-surface-raised px-6 py-5">
      <h2 className="text-sm font-bold">{'IP 許可リスト'}</h2>
      <p className="text-xs leading-relaxed text-ink-subtle">
        {
          '登録すると、この組織のログインと MCP の接続がここに挙げた範囲からだけに制限されます。ログイン中の端末にも即座にかかります。'
        }
      </p>

      {/* 保存後の再読み込みで表ごと消えないよう、初回だけ読み込み表示にする */}
      <StateBlock loading={loading && data === null} error={error}>
        <div className="flex flex-col gap-3">
          <p className="text-xs text-ink-muted">
            {'いまの接続元: '}
            <span className="font-mono text-ink">{data?.clientIp ?? '不明'}</span>
          </p>

          {entries.length === 0 ? (
            <p className="text-xs text-ink-subtle">
              {'いまは制限がありません。どの接続元からでも利用できます。'}
            </p>
          ) : (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>RANGE</Th>
                    <Th>LABEL</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.id}>
                      <Td className="font-mono">{entry.cidr}</Td>
                      <Td>{entry.label ?? ''}</Td>
                      <Td className="text-right">
                        {isAdmin ? (
                          <Button
                            variant="ghost"
                            onClick={() =>
                              run(
                                () => api.delete(`/api/tenant/ip-allowlist/${entry.id}`),
                                '削除しました',
                              )
                            }
                          >
                            削除
                          </Button>
                        ) : null}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </div>
      </StateBlock>

      {isAdmin ? <AddRow clientIp={data?.clientIp ?? null} onRun={run} /> : null}
      {message ? <p className="text-xs text-ink-muted">{message}</p> : null}
    </section>
  );
}

function AddRow({
  clientIp,
  onRun,
}: {
  clientIp: string | null;
  onRun: (action: () => Promise<unknown>, note: string) => Promise<void>;
}) {
  const [cidr, setCidr] = useState('');
  const [label, setLabel] = useState('');

  return (
    <FieldRow className="border-t border-line pt-4">
      <Field
        label="範囲を追加"
        hint="単一の IP でも CIDR でも指定できます (203.0.113.10 / 203.0.113.0/24)"
        className="min-w-64 flex-1"
      >
        <Input
          value={cidr}
          onChange={(e) => setCidr(e.target.value)}
          placeholder="203.0.113.0/24"
          className="font-mono"
        />
      </Field>
      <Field label="ラベル" className="min-w-48">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="東京本社" />
      </Field>
      {/* 最初の 1 件で自分を締め出さないよう、現在の IP をそのまま入れられるようにする */}
      {clientIp ? (
        <Button onClick={() => setCidr(clientIp)}>{'いまの接続元を入れる'}</Button>
      ) : null}
      <Button
        variant="primary"
        disabled={cidr.trim() === ''}
        onClick={() =>
          onRun(async () => {
            await api.post('/api/tenant/ip-allowlist', {
              cidr: cidr.trim(),
              label: label.trim(),
            });
            setCidr('');
            setLabel('');
          }, '追加しました')
        }
      >
        {/* 同じ画面のプロジェクト種別にも「追加」があるので、ここは別の語にする
            (押し間違いも、支援技術での読み上げの曖昧さも同じ理由で避けたい) */}
        {'範囲を登録'}
      </Button>
    </FieldRow>
  );
}
